import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { OrderDirection } from 'webapp/enums/enums';
import { uniq } from 'webapp/utils/common';
import type { SearchProps } from 'webapp/utils/filter';

import type BoardLoop from 'webapp/models-custom/board-loop';
import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import { RECENTLY_DELETED_ID, type FilterOption } from 'webapp/models-custom/filter-sort/filter';

export enum BoardLoopCategory {
	ALL = 0,
	FAVORITES = 1,
	DELETED = RECENTLY_DELETED_ID,
}

export enum BoardLoopFilterId {
	TAGS = 1,
	OWNER = 2,
	CATEGORY = 3,
	DATA_SOURCE = 4,
	CREATOR = 5,
}

export default class FilterSortBoardLoop extends FilterSort<BoardLoop> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('flow/promotions') declare flowPromotionsService: Service.Flow.Promotions;
	@tracked override sortProp = 'created';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.DESC;

	override searchProps: SearchProps = {
		title: 3,
		'ownerUser.name': 1,
		boardNamesText: 1,
	};

	override itemDomSelector = '.table-row';

	override settingsNamePrefix = 'boardLoopsFilterSettings';
	override trackingObjectName = 'board-loop';

	constructor(options: FilterSortSettings<BoardLoop>) {
		super(options);

		const selectedFilters = this.readSavedSelectedFilters();

		// Data source (used only)
		this.createFilter<string>({
			id: BoardLoopFilterId.DATA_SOURCE,
			label: 'Data source',
			name: 'data source',
			selected: selectedFilters[BoardLoopFilterId.DATA_SOURCE] as string | undefined,
			stringValues: true,
			isMultiSelect: true,
			getOptions: () => {
				return this.datasourceOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((sourceTypeKey) => {
					const sourceType = this.mmSourceTypesService.findModelByKey(sourceTypeKey);

					const board = item.boards.find((board) => {
						return (
							board.specificSourceTypesKeys.includes(sourceTypeKey) ||
							(sourceType?.id &&
								board.sourceTypeIdsMissingFromResolutionMessages.includes(
									sourceType.id,
								))
						);
					});

					return !!board;
				});
			},
		});

		// Owner
		this.createFilter<number>({
			id: BoardLoopFilterId.OWNER,
			label: 'Owned by',
			name: 'owner',
			selected: selectedFilters[BoardLoopFilterId.OWNER] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.ownerOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((ownerId) => {
					if (ownerId === -1) {
						return !item.isOwnedByMe;
					} else {
						return item.ownerId === ownerId;
					}
				});
			},
		});

		// Created by
		this.createFilter<number>({
			id: BoardLoopFilterId.CREATOR,
			label: 'Created by',
			name: 'creator',
			selected: selectedFilters[BoardLoopFilterId.CREATOR] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.creatorOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((creatorId) => {
					if (creatorId === -1) {
						return !item.isMine;
					} else {
						return item.createdBy === creatorId;
					}
				});
			},
		});

		// Category
		this.createFilter<number>({
			id: BoardLoopFilterId.CATEGORY,
			label: 'Show',
			name: 'category',
			selected: selectedFilters[BoardLoopFilterId.CATEGORY] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.categoryOptions;
			},
			onSelectedChange: (filter) => {
				this.flowPromotionsService.endRecentlyDeletedTour();

				if (filter.selected.includes(BoardLoopCategory.DELETED)) {
					void this.mmBoardLoopsService.requestDeletedBoardLoops();
				}
			},
			filterFn: function (item, selected) {
				return selected.some((categoryId) => {
					switch (categoryId) {
						case BoardLoopCategory.ALL:
							return !item.deleted;
						case BoardLoopCategory.FAVORITES:
							return item.favorite;
						case BoardLoopCategory.DELETED:
							return !!item.deleted && item.canCurrentUserEditWithoutDeleted;
						default:
							return true;
					}
				});
			},
		});

		const categoryFilter = this.findFilter(BoardLoopFilterId.CATEGORY);
		if (categoryFilter?.selected.includes(BoardLoopCategory.DELETED)) {
			void this.mmBoardLoopsService.requestDeletedBoardLoops();
		}

		// Tags (used only)
		this.createFilter<number>({
			id: BoardLoopFilterId.TAGS,
			label: 'Tags',
			name: 'tag',
			selected: selectedFilters[BoardLoopFilterId.TAGS] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.tagOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((tagId) => {
					return item.tags.includes(tagId);
				});
			},
		});
	}

	get tagOptions(): FilterOption<number>[] {
		const values = this.mmBoardLoopsService.assignedTags,
			arrayCopy = Array.from(values) as FilterOption<number>[];
		if (arrayCopy.length) {
			arrayCopy.unshift({ id: 0, text: 'All' });
		}
		return arrayCopy;
	}

	get ownerOptions(): FilterOption<number>[] {
		const allItemCountByOwner: Record<number, number> = {};
		this.allItems.forEach((item) => {
			const ownerId = item.ownerId || 0;
			if (!allItemCountByOwner[ownerId]) {
				allItemCountByOwner[ownerId] = 0;
			}
			allItemCountByOwner[ownerId]++;
		});

		return this.mmUsersService.getFilterOptionsForUsers(allItemCountByOwner);
	}

	get categoryOptions(): FilterOption<number>[] {
		// id of filters shouldn't be changed (only add or remove) because they are saved in users local storage
		return [
			{ id: BoardLoopCategory.ALL, text: 'All' },
			{
				id: BoardLoopCategory.FAVORITES,
				text: 'Favorites',
				icon: 'StarFill',
				color: 'var(--databox-yellow-500)',
			},
			{
				id: BoardLoopCategory.DELETED,
				text: 'Recently deleted',
				icon: 'Trash',
				class: 'top-divider',
				notCombinable: true,
			},
		];
	}

	@cached
	get creatorOptions(): FilterOption<number>[] {
		const allItemCountByCreator: Record<number, number> = {};
		this.allItems.forEach((item) => {
			const createdById = item.createdById;
			if (!allItemCountByCreator[createdById]) {
				allItemCountByCreator[createdById] = 0;
			}
			allItemCountByCreator[createdById]++;
		});

		return this.mmUsersService.getFilterOptionsForUsers(allItemCountByCreator);
	}

	@cached
	get datasourceOptions(): FilterOption<string>[] {
		// get how many boards each source is used on
		const sourceTypeArray: string[] = [];

		this.allItems.forEach((boardLoop: BoardLoop) => {
			boardLoop.boards.forEach((item) => {
				const sourcesFromBoard: string[] = [];
				item.specificSourceTypesKeys.forEach((key: string) => {
					if (key) {
						sourcesFromBoard.push(key);
					}
				});

				item.sourceTypeIdsMissingFromResolutionMessages.forEach((missingType: number) => {
					if (missingType) {
						const missingSource = this.mmSourceTypesService.findModelById(missingType);

						if (missingSource) {
							sourcesFromBoard.push(missingSource.key);
						}
					}
				});
				uniq(sourcesFromBoard).forEach((key: string) => {
					sourceTypeArray.push(key);
				});
			});
		});

		const list = this.mmSourceTypesService.getSourceTypesListWithCount(sourceTypeArray);

		list.unshift({ id: 'all', text: 'All' });

		return list;
	}

	@cached
	override get results(): BoardLoop[] {
		const baseResults = super.results;

		// sort favorites first, then non-favorites
		return baseResults.sort((a, b) => {
			if (a.favorite === b.favorite) {
				return 0;
			}
			return a.favorite ? -1 : 1;
		});
	}
}
